import React from 'react';
import { Dialog, DialogContent, DialogActions, Typography, useTheme } from '@mui/material';
import { useStartFeatureAnalysesImportMutation } from 'src/redux/services/imports';
import CloseIcon from '@mui/icons-material/Close';
import ButtonComponent from 'src/components/ButtonComponent';
import { getAccessToken } from 'src/frontend-common/localstorage';
import { BASE_URL } from 'src/redux/services/utils';
import { ExportHeaderRow } from 'src/style/common';
import AppkeyAutocomplete from 'src/components/AppkeyAutocomplete';
import DragAndDropFiles from 'src/components/DragAndDropFiles';

export interface InsightsModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  validatingFile: boolean;
  setValidatingFile: React.Dispatch<React.SetStateAction<boolean>>;
}

const InsightsModal: React.FC<InsightsModalProps> = ({ openModal, setOpenModal, setShowSpinner, validatingFile, setValidatingFile }) => {
  const [validatedFileID, setValidatedFileID] = React.useState<string | undefined>(undefined);
  const [selectedAppkey, setSelectedAppkey] = React.useState<{
    label: string;
    key: string;
  } | null>(null);

  const theme = useTheme();

  const handleClose = () => {
    setOpenModal(false);
  };

  const token = getAccessToken();

  const [startFeatureAnalysesImport] = useStartFeatureAnalysesImportMutation();

  const importFile = () => {
    startFeatureAnalysesImport(validatedFileID);
    setOpenModal(false);
    setShowSpinner(true);
    setTimeout(() => setShowSpinner(false), 4000);
  };

  const doFetchDownload = () => {
    fetch(`${BASE_URL}/v1/feature-analyses/example`, {
      headers: { Authorization: token || '' },
    })
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // the filename you want
        a.download = 'example.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => console.log('error'));
  };

  return (
    <Dialog fullWidth={true} maxWidth="sm" open={openModal} onClose={handleClose}>
      <ExportHeaderRow>
        <Typography variant="h4">Upload</Typography>
        <ButtonComponent icon={<CloseIcon />} buttontype="icon" onClick={handleClose} />
      </ExportHeaderRow>
      <DialogContent style={{ overflow: 'hidden', paddingTop: theme.spacing(3) }}>
        <Typography variant="h4">App key</Typography>
        <AppkeyAutocomplete selectedAppkey={selectedAppkey} setSelectedAppkey={setSelectedAppkey} sx={{ marginBottom: theme.spacing(2) }} activeOnly={true} />
        <Typography variant="h5" style={{ marginBottom: theme.spacing(1.125) }}>
          How to upload
        </Typography>
        <Typography color="text.secondary">
          1. Download our example
          <span style={{ color: theme.palette.text.primary, cursor: 'pointer', marginLeft: theme.spacing(0.5) }} onClick={doFetchDownload}>
            CSV file.
          </span>
        </Typography>
        <Typography color="text.secondary">2. Add your data to our template and make sure to export or save as a .csv file.</Typography>
        <Typography color="text.secondary">3. Upload it below, for processing.</Typography>
        <DragAndDropFiles
          setValidatingFile={setValidatingFile}
          setValidatedFileID={setValidatedFileID}
          isFeatureAnalyses={true}
          appkey={selectedAppkey?.key || ''}
        />
        <DialogActions>
          <ButtonComponent buttontype="cancel" onClick={handleClose} style={{ marginRight: theme.spacing(2) }} />
          <ButtonComponent buttontype="mainaction" text="Import" onClick={importFile} disabled={validatingFile} />
        </DialogActions>{' '}
      </DialogContent>
    </Dialog>
  );
};

export default InsightsModal;
